import { Fragment } from 'react';
import { red } from '@ahora-patagonia/design-tokens';

interface Mark {
  type: 'bold' | 'italic' | 'strike' | 'link';
  attrs?: { href: string; target?: '_blank' | '_self' | null; rel?: string | null };
}

export interface InlineNode {
  type: 'text' | 'hardBreak';
  text?: string;
  marks?: Mark[];
}

/**
 * Texto en línea del cuerpo: `text` con sus marcas y `hardBreak`. **Nunca HTML**: el texto
 * es texto (React lo escapa) y las marcas se componen como elementos.
 *
 * El link del cuerpo es el único link del sitio con subrayado permanente (1 px, offset 2)
 * y va en rojo 700: dentro de una columna de lectura, un link sin subrayado es invisible.
 */
export function InlineTexto({ nodos }: { nodos: InlineNode[] | undefined }) {
  if (!nodos) return null;
  return (
    <>
      {nodos.map((nodo, i) => (
        <Fragment key={i}>{renderInline(nodo)}</Fragment>
      ))}
    </>
  );
}

function renderInline(nodo: InlineNode) {
  if (nodo.type === 'hardBreak') return <br />;
  let out: React.ReactNode = nodo.text ?? '';
  for (const mark of nodo.marks ?? []) {
    if (mark.type === 'bold') out = <strong>{out}</strong>;
    if (mark.type === 'italic') out = <em>{out}</em>;
    if (mark.type === 'strike') out = <s>{out}</s>;
    if (mark.type === 'link' && mark.attrs) {
      const target = mark.attrs.target ?? '_blank';
      out = (
        <a
          href={mark.attrs.href}
          target={target}
          // rel seguro siempre, se pida lo que se pida: el cuerpo viene de la redacción,
          // pero un destino externo nunca recibe el opener.
          rel={target === '_blank' ? 'noopener noreferrer' : (mark.attrs.rel ?? undefined)}
          style={{
            color: red[700],
            textDecoration: 'underline',
            textDecorationThickness: '1px',
            textUnderlineOffset: '2px',
          }}
        >
          {out}
        </a>
      );
    }
  }
  return out;
}
